/**
 * OperacionPage — Consumo por operación (pasadía/charter).
 * Lista operaciones del día con estado "pendiente" o "registrado",
 * y formulario para capturar horómetro + galones reales al terminar.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, TextField, Chip, Button, IconButton, Tooltip,
  CircularProgress, Snackbar, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

function getEmpresaId() {
  // Superadmin puede cambiar de empresa desde el sidebar (Layout escribe
  // `empresa_activa` en localStorage). Respetar ese valor por encima de
  // `user.company_id` para no quedar fuera de sync con el selector activo.
  const activa = localStorage.getItem('empresa_activa');
  if (activa) return activa;
  try { const u = JSON.parse(localStorage.getItem('user') || '{}'); return u.company_id || u.empresa_id || '1'; }
  catch { return '1'; }
}
function getAuthHeaders() {
  const t = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
}
const hoyStr = () => new Date().toISOString().slice(0, 10);
const fmtGal = (n) => n == null ? '—' : Number(n).toLocaleString('es-CO', { maximumFractionDigits: 2 }) + ' gal';

const ALERTA_COLOR = {
  verde:    { bg: '#dcfce7', fg: '#166534' },
  amarillo: { bg: '#fef3c7', fg: '#92400e' },
  rojo:     { bg: '#fee2e2', fg: '#991b1b' },
  gris:     { bg: '#f1f5f9', fg: '#475569' },
};

export default function OperacionPage() {
  const empresaId = getEmpresaId();
  const [fecha, setFecha] = useState(hoyStr());
  const [pendientes, setPendientes] = useState([]);
  const [historico, setHistorico] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, op: null });
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [rP, rH] = await Promise.all([
        fetch(`${API_BASE}/turismo/combustible/consumos/pendientes?empresa_id=${empresaId}&fecha=${fecha}`,
          { headers: getAuthHeaders() }),
        fetch(`${API_BASE}/turismo/combustible/consumos?empresa_id=${empresaId}&fecha_desde=${fecha}&fecha_hasta=${fecha}`,
          { headers: getAuthHeaders() }),
      ]);
      if (rP.ok) setPendientes(await rP.json());
      if (rH.ok) setHistorico(await rH.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [empresaId, fecha]);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirRegistrar = (op) => {
    setForm({
      tipo_operacion: op.tipo_operacion,
      salida_id: op.salida_id,
      reservacion_id: op.reservacion_id,
      embarcacion_id: op.embarcacion_id,
      embarcacion_nombre: op.embarcacion_nombre,
      operacion_label: op.operacion_label,
      consumo_hora_estandar: op.consumo_hora_estandar,
      horas_motor_actuales: op.horas_motor_actuales,
      horas_motor_inicio: op.horas_motor_actuales != null ? String(op.horas_motor_actuales) : '',
      horas_motor_fin: '',
      horas_operadas: '',
      galones_reales: '',
      notas: '',
    });
    setDialog({ open: true, op });
  };

  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const horasCalculadas = (() => {
    const ini = Number(form.horas_motor_inicio);
    const fin = Number(form.horas_motor_fin);
    if (ini >= 0 && fin >= ini) return (fin - ini).toFixed(2);
    return form.horas_operadas || '';
  })();

  const galonesEstimados = (() => {
    const h = Number(horasCalculadas);
    const c = Number(form.consumo_hora_estandar);
    return h > 0 && c > 0 ? (h * c).toFixed(2) : null;
  })();

  const guardar = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const body = {
        empresa_id: empresaId,
        embarcacion_id: form.embarcacion_id,
        tipo_operacion: form.tipo_operacion,
        salida_id: form.salida_id || null,
        reservacion_id: form.reservacion_id || null,
        fecha_operacion: fecha,
        horas_motor_inicio: form.horas_motor_inicio ? Number(form.horas_motor_inicio) : null,
        horas_motor_fin: form.horas_motor_fin ? Number(form.horas_motor_fin) : null,
        horas_operadas: horasCalculadas ? Number(horasCalculadas) : null,
        galones_reales: form.galones_reales ? Number(form.galones_reales) : null,
        notas: form.notas.trim() || null,
      };
      const r = await fetch(`${API_BASE}/turismo/combustible/consumos`, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) {
        setSnack({ open: true, msg: d.detail?.message || d.detail || 'Error', severity: 'error' });
        return;
      }
      setSnack({ open: true, msg: `Consumo registrado: ${form.galones_reales || '—'} gal`, severity: 'success' });
      setDialog({ open: false, op: null });
      cargar();
    } catch (e) {
      setSnack({ open: true, msg: 'Error de red', severity: 'error' });
    } finally { setSaving(false); }
  };

  const soloPend = pendientes.filter(p => p.estado === 'pendiente');
  const yaReg = pendientes.filter(p => p.estado === 'registrado');

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 13, color: '#64748b' }}>
            Registra al final de cada operación los galones consumidos y (si tienes horómetro) las horas de motor.
          </Typography>
        </Box>
        <TextField size="small" type="date" label="Día" InputLabelProps={{ shrink: true }}
          value={fecha} onChange={(e) => setFecha(e.target.value)} sx={{ width: 160 }} />
        <Tooltip title="Refrescar"><IconButton onClick={cargar}><RefreshIcon /></IconButton></Tooltip>
      </Box>

      {/* Pendientes del día */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <PendingIcon sx={{ color: '#f59e0b' }} />
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#0f172a', flex: 1 }}>
            Operaciones pendientes de registrar
          </Typography>
          <Chip label={`${soloPend.length}`} size="small"
            sx={{ bgcolor: soloPend.length > 0 ? '#fef3c7' : '#dcfce7',
                  color: soloPend.length > 0 ? '#92400e' : '#166534',
                  fontWeight: 700 }}
          />
        </Box>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} sx={{ color: '#dc2626' }} />
          </Box>
        ) : soloPend.length === 0 ? (
          <Typography sx={{ fontSize: 12, color: '#94a3b8', py: 2, textAlign: 'center' }}>
            ✅ Sin pendientes. Todas las operaciones del día tienen consumo registrado.
          </Typography>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5 }}>
            {soloPend.map((op, idx) => (
              <Paper key={idx} sx={{ p: 1.5, border: '1px solid #f59e0b', bgcolor: '#fffbeb' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                  <DirectionsBoatIcon sx={{ color: '#0d9488', fontSize: 18 }} />
                  <Typography sx={{ fontSize: 13, fontWeight: 700, flex: 1 }}>
                    {op.embarcacion_nombre}
                  </Typography>
                  <Chip label={op.tipo_operacion === 'pasadia' ? 'Pasadía' : 'Charter'} size="small"
                    sx={{
                      bgcolor: op.tipo_operacion === 'pasadia' ? '#dbeafe' : '#ede9fe',
                      color: op.tipo_operacion === 'pasadia' ? '#1e40af' : '#5b21b6',
                      fontSize: 10, fontWeight: 700,
                    }}
                  />
                </Box>
                <Typography sx={{ fontSize: 12, color: '#475569', mb: 1 }}>
                  {op.operacion_label} · {op.hora || '—'}
                </Typography>
                <Button fullWidth variant="contained" size="small"
                  startIcon={<AddCircleIcon />} onClick={() => abrirRegistrar(op)}
                  sx={{
                    textTransform: 'none', fontWeight: 700,
                    background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                  }}
                >
                  Registrar consumo
                </Button>
              </Paper>
            ))}
          </Box>
        )}
      </Paper>

      {/* Ya registrados */}
      {yaReg.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <CheckCircleIcon sx={{ color: '#16a34a' }} />
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#0f172a', flex: 1 }}>
              Ya registradas
            </Typography>
            <Chip label={`${yaReg.length}`} size="small"
              sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 700 }}
            />
          </Box>
        </Paper>
      )}

      {/* Histórico de consumos del día */}
      {historico.length > 0 && (
        <Paper>
          <Box sx={{ p: 1.5, borderBottom: '1px solid #e2e8f0' }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a', textTransform: 'uppercase' }}>
              Consumos registrados · {fecha}
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#f8fafc' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Operación</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Embarcación</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Horas</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Est.</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Real</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Desv.</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Costo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historico.map(c => {
                  const a = ALERTA_COLOR[c.nivel_alerta] || ALERTA_COLOR.gris;
                  return (
                    <TableRow key={c.id} hover>
                      <TableCell sx={{ fontSize: 12 }}>{c.operacion_label}</TableCell>
                      <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>{c.embarcacion_nombre}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 12 }}>
                        {c.horas_operadas != null ? `${c.horas_operadas} h` : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 12, color: '#64748b' }}>
                        {fmtGal(c.galones_estimados)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 12, fontWeight: 700 }}>
                        {fmtGal(c.galones_reales)}
                      </TableCell>
                      <TableCell align="right">
                        {c.desviacion_pct != null && (
                          <Chip label={`${c.desviacion_pct > 0 ? '+' : ''}${c.desviacion_pct}%`} size="small"
                            sx={{ bgcolor: a.bg, color: a.fg, fontWeight: 700, fontSize: 10.5, height: 22 }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 12 }}>
                        {c.costo_real ? `$${Number(c.costo_real).toLocaleString('es-CO')}` : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Dialog registrar consumo */}
      <Dialog open={dialog.open} onClose={() => setDialog({ open: false, op: null })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
          <LocalGasStationIcon sx={{ color: '#dc2626' }} />
          Registrar consumo
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ fontSize: 12, mb: 2, mt: 1 }}>
            <strong>{form.operacion_label}</strong> · {form.embarcacion_nombre}
            {form.consumo_hora_estandar && (
              <> · consumo estándar: {form.consumo_hora_estandar} gal/h</>
            )}
          </Alert>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', mb: 1, textTransform: 'uppercase' }}>
            Horómetro (opcional)
          </Typography>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="number" label="Horas al inicio"
                value={form.horas_motor_inicio} onChange={set('horas_motor_inicio')}
                inputProps={{ step: 0.1, min: 0 }}
                helperText={form.horas_motor_actuales ? `Actual: ${form.horas_motor_actuales}` : ' '}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="number" label="Horas al final"
                value={form.horas_motor_fin} onChange={set('horas_motor_fin')}
                inputProps={{ step: 0.1, min: 0 }}
                helperText={horasCalculadas ? `Δ ${horasCalculadas} h` : ' '}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" type="number" label="Horas operadas (si no tienes horómetro)"
                value={form.horas_operadas} onChange={set('horas_operadas')}
                inputProps={{ step: 0.25, min: 0 }}
                disabled={!!(form.horas_motor_inicio && form.horas_motor_fin)}
              />
            </Grid>
          </Grid>

          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', mb: 1, textTransform: 'uppercase' }}>
            Galones consumidos
          </Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="number" label="Galones reales *"
                value={form.galones_reales} onChange={set('galones_reales')}
                inputProps={{ step: 0.1, min: 0 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Galones estimados"
                value={galonesEstimados != null ? `${galonesEstimados} gal` : '(sin datos)'}
                InputProps={{ readOnly: true }}
                helperText="Auto: horas × consumo estándar"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" multiline rows={2} label="Notas"
                value={form.notas} onChange={set('notas')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog({ open: false, op: null })} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={guardar} disabled={!form.galones_reales || saving}
            sx={{
              textTransform: 'none', fontWeight: 700,
              background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
            }}
          >
            {saving ? 'Guardando…' : 'Guardar consumo'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
